import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import '../admin/Admin.css';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';

const API_BASE = `${QUALITY_SERVICE_API_URL}/hub/sense-shield/known-issues`;

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// A test only becomes a known-issue candidate after Sense Shield has already reported a result
// for it, so `options` (from GET .../known-issues/test-ids) always covers every valid choice —
// a checkbox dropdown is a better fit than free text here: no typos, nothing to type at all.
function TestIdMultiSelect({ value, onChange, options }) {
  const toggle = (testId) => {
    onChange(value.includes(testId) ? value.filter((t) => t !== testId) : [...value, testId]);
  };

  return (
    <div className="cf-multiselect">
      <details className="cf-multiselect-details">
        <summary className="cf-multiselect-summary">
          {value.length === 0 ? 'Select test IDs…' : `${value.length} test${value.length === 1 ? '' : 's'} selected`}
        </summary>
        <div className="cf-multiselect-panel">
          {options.length === 0 && <div className="cf-multiselect-empty">No tests reported by Sense Shield yet.</div>}
          {options.map((testId) => (
            <label key={testId} className="cf-multiselect-option">
              <input type="checkbox" checked={value.includes(testId)} onChange={() => toggle(testId)} />
              {testId}
            </label>
          ))}
        </div>
      </details>

      {value.length > 0 && (
        <div className="pill-input cf-multiselect-selected">
          {value.map((testId) => (
            <span key={testId} className="pill-input-tag">
              {testId}
              <button type="button" className="pill-input-remove" onClick={() => toggle(testId)}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function KnownIssueForm({ knownIssue, availableTestIds, onSave, onCancel, saving, error }) {
  const isNew = !knownIssue.jiraTicket;
  const [jiraTicket, setJiraTicket] = useState(knownIssue.jiraTicket ?? '');
  const [testIds, setTestIds] = useState(knownIssue.testIds ?? []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ jiraTicket, testIds });
  };

  return (
    <div className="cf-overlay">
      <div className="cf-modal cf-modal-known-issue">
        <div className="cf-header">
          <h2>{isNew ? 'New Known Issue' : 'Edit Known Issue'}</h2>
          <button className="cf-close" type="button" onClick={onCancel}>✕</button>
        </div>

        <form className="cf-form cf-form-known-issue" onSubmit={handleSubmit}>
          <label className="cf-label">Jira Ticket <span className="cf-req">*</span></label>
          <input
            className="cf-input"
            required
            value={jiraTicket}
            onChange={(e) => setJiraTicket(e.target.value)}
            placeholder="e.g. QA-1234 — must already exist in Jira"
          />

          <label className="cf-label">Test IDs <span className="cf-req">*</span></label>
          <TestIdMultiSelect value={testIds} onChange={setTestIds} options={availableTestIds} />

          {error && <div className="admin-error">{error}</div>}

          <div className="cf-actions">
            <button type="button" className="cf-btn-cancel" onClick={onCancel}>Cancel</button>
            <button type="submit" className="cf-btn-save" disabled={saving || testIds.length === 0}>
              {saving ? 'Saving…' : isNew ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function KnownIssuesPanel() {
  const [knownIssues, setKnownIssues] = useState([]);
  const [availableTestIds, setAvailableTestIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listError, setListError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = editing

  const load = () => {
    setLoading(true);
    api.get(API_BASE)
      .then((r) => setKnownIssues(r.data))
      .catch(() => setListError('Failed to load known issues.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);
  useEffect(() => {
    api.get(`${API_BASE}/test-ids`).then((r) => setAvailableTestIds(r.data)).catch(() => setAvailableTestIds([]));
  }, []);

  const handleSave = async (data) => {
    setSaving(true);
    setFormError(null);
    try {
      if (editing.jiraTicket) {
        await api.put(`${API_BASE}/${encodeURIComponent(editing.jiraTicket)}`, data);
      } else {
        await api.post(API_BASE, data);
      }
      setEditing(null);
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Save failed. Please check the Jira ticket and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (knownIssue) => {
    if (!window.confirm(`Remove the known-issue mapping for "${knownIssue.jiraTicket}"?`)) return;
    try {
      await api.delete(`${API_BASE}/${encodeURIComponent(knownIssue.jiraTicket)}`);
      load();
    } catch {
      setListError('Delete failed.');
    }
  };

  return (
    <div>
      <div className="sense-shield-panel-title sense-shield-panel-title-with-action">
        <span>Known Issues</span>
        <button className="admin-btn-primary" onClick={() => { setFormError(null); setEditing({}); }}>+ New Known Issue</button>
      </div>
      <div className="sense-shield-panel-content">
        <p className="sense-shield-panel-description">
          Tests mapped here are excluded from the Slack noise for Sense Shield warnings/failures, while still counting toward the run totals.
        </p>

        {listError && <div className="admin-error">{listError}</div>}

        {loading ? (
          <div className="sense-shield-empty">Loading...</div>
        ) : (
          <div className="sense-shield-table-wrapper">
            <table className="sense-shield-table">
              <thead>
                <tr>
                  <th>Jira Ticket</th>
                  <th>Test IDs</th>
                  <th>Created By</th>
                  <th>Created At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {knownIssues.map((k) => (
                  <tr key={k.jiraTicket}>
                    <td><a href={k.jiraUrl} target="_blank" rel="noreferrer">{k.jiraTicket}</a></td>
                    <td>{k.testIds.join(', ')}</td>
                    <td>{k.createdByEmail || '—'}</td>
                    <td>{fmt(k.createdAt)}</td>
                    <td className="admin-td-actions">
                      <button className="admin-btn-edit" onClick={() => { setFormError(null); setEditing(k); }}>Edit</button>
                      <button className="admin-btn-delete" onClick={() => handleDelete(k)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {knownIssues.length === 0 && (
                  <tr>
                    <td colSpan={5} className="sense-shield-empty">No known issues mapped yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing !== null && (
        <KnownIssueForm
          knownIssue={editing}
          availableTestIds={availableTestIds}
          onSave={handleSave}
          onCancel={() => { setEditing(null); setFormError(null); }}
          saving={saving}
          error={formError}
        />
      )}
    </div>
  );
}

export default KnownIssuesPanel;
